import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useSelectionMap } from '@hooks';

import { LabelChangeTarget } from '@app/features/label/components/LabelPopovers/context/types';
import { MAX_COUNT_OF_LABELS_PER_ONE_TIME_APPLY } from '@app/features/workspaceManagement/constants';

export type LabelChangesContextType = {
  labelChangesMap: Map<string, LabelChangeTarget>;
  add: (labelId: string, checked: LabelChangeTarget) => void;
  remove: (labelId: string) => void;
  hasExceedChangeLimit: boolean;
};

const CHANGE_LIMIT = MAX_COUNT_OF_LABELS_PER_ONE_TIME_APPLY;

// 라벨 적용 맥락에서 사용할 라벨링 상태를 관리하는 컨텍스트
const LabelChangesContext = createContext<LabelChangesContextType>({
  labelChangesMap: new Map(),
  add: () => {},
  remove: () => {},
  hasExceedChangeLimit: false,
});

LabelChangesContext.displayName = 'LabelChangesContext';

export const useLabelChangeContext = () => {
  const context = useContext(LabelChangesContext);

  if (!context) {
    throw new Error('LabelChangesContext must be used within a LabelChangesContextProvider');
  }

  return context;
};

export function LabelChangesContextProvider({ children }: { children: ReactNode }) {
  const { map, entries, add, remove } = useSelectionMap<string, LabelChangeTarget>();

  const labelChangesContext = useMemo<LabelChangesContextType>(
    () => ({
      labelChangesMap: map,
      add: (labelId: string, target: LabelChangeTarget) => {
        add(labelId, target);
      },
      remove: (labelId: string) => {
        remove(labelId);
      },
      hasExceedChangeLimit: entries.length >= CHANGE_LIMIT,
    }),
    [add, entries, map, remove]
  );

  return (
    <LabelChangesContext.Provider value={labelChangesContext}>
      {children}
    </LabelChangesContext.Provider>
  );
}
